import { type EventWithSeatsAndTickets } from '#app/utils/providers/events.server.js'
import { formatClock, formatWeekday } from './festival-time.ts'

const toDate = (date: Date | string) =>
	date instanceof Date ? date : parseDate(date)

export const formatDate = (date: Date | string) => {
	try {
		const parsedDate = toDate(date)
		return `${formatWeekday(parsedDate)} kl. ${formatClock(parsedDate)}`
	} catch (error) {
		console.error(`Error formatting date: ${date}`, error)
		return 'Ugyldig dato' // Return 'Invalid date' in Norwegian
	}
}

export const formatTime = (date: Date | string) => {
	const parsedDate = toDate(date)
	return new Intl.DateTimeFormat('no-NO', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	}).format(parsedDate)
}

export const isEventPassed = (eventDate: string | Date): boolean => {
	try {
		const date = eventDate instanceof Date ? eventDate : parseDate(eventDate)
		return new Date() > date
	} catch (error) {
		console.error('Error in isEventPassed:', error)
		return false // Assume event hasn't passed if we can't determine
	}
}

/** When an event is over: its end, or its start when only that is known. */
export const eventEnd = (event: {
	date: string | Date
	endsAt?: string | Date | null
}) => event.endsAt ?? event.date

export const groupEventsByDate = (
	events: EventWithSeatsAndTickets[],
): Record<string, EventWithSeatsAndTickets[]> => {
	return events.reduce(
		(groups, event) => {
			let dateString: string
			try {
				const date =
					event.date instanceof Date ? event.date : parseDate(event.date)
				dateString = date.toDateString()
			} catch (error) {
				console.error(`Error processing date for event:`, event, error)
				dateString = 'Invalid Date'
			}

			if (!groups[dateString]) {
				groups[dateString] = []
			}
			groups[dateString]!.push(event)
			return groups
		},
		{} as Record<string, EventWithSeatsAndTickets[]>,
	)
}

export const getGoogleMapsEmbedUrl = (location: string, apiKey: string) => {
	const encodedLocation = encodeURIComponent(location)
	return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodedLocation}&zoom=15`
}

export const getTicketStatus = (seats: {
	total: number
	remaining: number
}) => {
	if (seats.remaining === 0) return 'soldOut'
	if (seats.remaining <= 10) return 'limited'
	return 'available'
}

export const parseDate = (dateString: string): Date => {
	try {
		const parsedDate = new Date(dateString)
		if (isNaN(parsedDate.getTime())) {
			throw new Error('Invalid date')
		}
		return parsedDate
	} catch (error) {
		console.error(`Error parsing date: ${dateString}`, error)
		return new Date() // Fallback to current date
	}
}

/**
 * Display name for who performs: linked bio-pages win, free-text is the
 * fallback. The links have no order of their own, so they follow the order
 * the names have in the free-text (unmentioned ones last, order kept).
 */
export const performersDisplay = (
	event: Pick<EventWithSeatsAndTickets, 'performers' | 'featuring'>,
) => {
	if (!event.featuring?.length) return event.performers ?? null
	const text = (event.performers ?? '').toLowerCase()
	const position = (name: string) => {
		const at = text.indexOf(name.toLowerCase())
		return at === -1 ? Infinity : at
	}
	return [...event.featuring]
		.sort((a, b) => position(a.name) - position(b.name))
		.map((p) => p.name)
		.join(', ')
}

export const splitByDays = (groups: [string, EventWithSeatsAndTickets[]][]) => {
	const dayCount = groups.length
	const first = groups.filter((_, i) => i % 2 === 0)
	const second = groups.filter((_, i) => i % 2 === 1)
	// spread days over two panels as evenly as possible
	if (dayCount <= 1) return [groups, []]
	if (dayCount === 2) return [groups.slice(0, 1), groups.slice(1)]
	if (dayCount === 3) return [groups.slice(0, 2), groups.slice(2)]
	return [first, second]
}
